import { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import type { UserRole } from '@aegislab/shared';
import { roleLabels } from '@/shared/config';
import { useDismiss } from '@/shared/lib/hooks';
import * as s from './RoleMultiSelect.css';

const ALL_ROLES: UserRole[] = ['PLAYER', 'OPERATOR', 'ADMIN'];

export interface RoleMultiSelectProps {
  value: UserRole[];
  onChange: (role: UserRole) => void;
  disabled?: boolean;
}

export function RoleMultiSelect({ value, onChange, disabled }: RoleMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePos();
    // body 로 portal 되므로 포커스를 직접 넘겨야 키보드가 목록에 닿는다.
    dropdownRef.current?.querySelector('input')?.focus();
  }, [open, updatePos]);

  useDismiss(
    dropdownRef,
    () => {
      setOpen(false);
      triggerRef.current?.focus();
    },
    { enabled: open, outsideClick: false, escape: true }
  );

  return (
    <div className={s.wrapper}>
      <button
        aria-expanded={open}
        ref={triggerRef}
        type="button"
        className={`${s.trigger}${open ? ` ${s.triggerOpen}` : ''}`}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-label="권한 관리"
      >
        {value.map((role) => (
          <span key={role} className={s.badge}>
            {roleLabels[role]}
          </span>
        ))}
        <CaretDown
          size={12}
          weight="bold"
          className={`${s.caret}${open ? ` ${s.caretOpen}` : ''}`}
        />
      </button>

      {open &&
        createPortal(
          <>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- 배경 막은 마우스 바깥 클릭 전용이다. 키보드는 useDismiss 의 Esc 가 닫고 포커스를 트리거로 돌려준다 */}
            <div
              className={s.overlay}
              data-testid="role-multiselect-overlay"
              onClick={() => setOpen(false)}
            />
            <div
              ref={dropdownRef}
              className={s.dropdown}
              role="group"
              aria-label="권한"
              style={{ top: pos.top, left: pos.left }}
            >
              {ALL_ROLES.map((role) => {
                const checked = value.includes(role);
                const id = `role-cb-${role}`;
                return (
                  <label key={role} className={s.checkboxRow} htmlFor={id}>
                    <input
                      id={id}
                      type="checkbox"
                      checked={checked}
                      onChange={() => onChange(role)}
                      aria-label={roleLabels[role]}
                    />
                    <span className={s.checkboxLabel}>{roleLabels[role]}</span>
                  </label>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
